import React, { useMemo, useRef, useState } from "react"
import moment from "moment"
import { Popover, Typography, Box } from "@mui/material"
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import { YearCalendar } from "@mui/x-date-pickers"
import { MultiSectionDigitalClock } from "@mui/x-date-pickers/MultiSectionDigitalClock"
import { trans } from "@/_providers/Translation"
import { useAuthDataContext } from "@/_providers/Auth"

// Конфигурира moment локала на текущия език с преводите от _langs/*.json,
// без да променя глобалния локал на moment.
const configureMomentLocale = (locale) => {
  const config = {
    months: trans("dates.months"),
    monthsShort: trans("dates.monthsShort"),
    monthsParseExact: true,
    weekdays: trans("dates.weekdays"),
    weekdaysShort: trans("dates.weekdaysShort"),
    weekdaysMin: trans("dates.weekdaysMin"),
    weekdaysParseExact: true,
    week: {
      dow: 1,
      doy: 4,
    },
  }

  const globalLocale = moment.locale()

  if (moment.locales().includes(locale)) {
    moment.updateLocale(locale, config)
  } else {
    moment.defineLocale(locale, config)
  }

  moment.locale(globalLocale)
}

export default function DatePicker({
  date,
  onDateChange,
  disabled,
  yearOnly = false,
  placeholder,
  fullDateFormat,
  maxDate = null,
  minDate = null,
  // name - рендерира скрит input, за да може стойността да влезе в new FormData(form)
  name,
  // validate - маркира полето като невалидно (глобалния .validate клас)
  validate = false,
  // error/helperText - същото поведение като partials/Input (червена рамка + съобщение под полето)
  error = false,
  helperText,
  className = "",
  fullWidth = false,
  id,
  // withTime - работи като input type="datetime-local" (стойност YYYY-MM-DDTHH:mm)
  withTime = false,
  // minutesStep - стъпка на минутите (native datetime-local стъпката е 60 сек. = 1 мин.)
  minutesStep = 1,
  // required - native валидация на формата (hidden input-ите не се валидират от браузъра)
  required = false,
}) {
  const { lang } = useAuthDataContext()
  const locale = lang() || "bg"

  useMemo(() => configureMomentLocale(locale), [locale])

  const containerRef = useRef(null)
  const [anchorEl, setAnchorEl] = useState(null)
  // при withTime изборът се пази тук, докато не се потвърди
  const [draft, setDraft] = useState(null)

  const displayFormat = fullDateFormat || (withTime ? "DD MMM, YY HH:mm" : "DD MMM, YY")

  const formatDate = (dateString) => {
    if (!dateString) return ""
    if (yearOnly) return `${dateString} г.`
    return moment(dateString).locale(locale).format(displayFormat)
  }

  const handleDateChange = (newValue) => {
    const newDate = newValue
      ? newValue.format(yearOnly ? "YYYY" : "YYYY-MM-DD")
      : null
    onDateChange?.(newDate)
    handleClose()
  }

  const handleDraftDayChange = (newValue) => {
    if (!newValue) return
    setDraft((prev) =>
      newValue.clone().set({
        hour: prev ? prev.hour() : 0,
        minute: prev ? prev.minute() : 0,
        second: 0,
        millisecond: 0,
      })
    )
  }

  const handleDraftTimeChange = (newValue) => {
    if (!newValue) return
    setDraft(newValue.clone().set({ second: 0, millisecond: 0 }))
  }

  const handleConfirm = () => {
    onDateChange?.(draft ? draft.format("YYYY-MM-DDTHH:mm") : null)
    handleClose()
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleContainerClick = () => {
    if (disabled) return
    if (withTime) {
      setDraft(
        (date ? moment(date) : moment())
          .locale(locale)
          .set({ second: 0, millisecond: 0 })
      )
    }
    setAnchorEl(containerRef.current)
  }

  const open = Boolean(anchorEl)

  const picker = (
    <div
      id={id}
      className={["date-range-picker", (validate || error) && "validate", className].filter(Boolean).join(" ")}
      ref={containerRef}
      onClick={handleContainerClick}
      style={{
        pointerEvents: disabled ? "none" : "auto",
        opacity: disabled ? 0.4 : 1,
        ...(fullWidth ? { width: "100%" } : {}),
      }}
    >
      {required ? (
        <input
          tabIndex={-1}
          name={name}
          value={date || ""}
          onChange={() => {}}
          disabled={disabled}
          required
          style={{
            position: "absolute",
            left: "50%",
            bottom: 0,
            width: 1,
            height: 1,
            padding: 0,
            border: 0,
            opacity: 0,
            pointerEvents: "none",
          }}
        />
      ) : name ? (
        <input type="hidden" name={name} value={date || ""} disabled={disabled} />
      ) : null}
      <div className="date-range-display">
        {!date || !String(date).length ? (
          <span style={{ paddingLeft: "5px" }}>
            {placeholder || trans("random.select")}
          </span>
        ) : (
          <span
            className="date-range-start"
            style={withTime ? { width: "auto" } : undefined}
          >
            {formatDate(date)}
          </span>
        )}
      </div>
    </div>
  )

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
      {helperText ? (
        <div className="custom-input">
          {picker}
          <div className="helper-text">{helperText}</div>
        </div>
      ) : (
        picker
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{ zIndex: 999999 }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 2,
            },
          },
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ pt: 2, px: 3, color: "text.secondary", fontWeight: 600 }}
        >
          {trans("random.selectDate")}
        </Typography>
        <Box>
          {yearOnly ? (
            <YearCalendar
              value={date ? moment(date, "YYYY").locale(locale) : null}
              onChange={handleDateChange}
              maxDate={maxDate}
              minDate={minDate}
            />
          ) : withTime ? (
            <>
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <DateCalendar
                  value={draft}
                  onChange={handleDraftDayChange}
                  maxDate={maxDate}
                  minDate={minDate}
                  sx={{ flexShrink: 0, m: 0 }}
                />
                <MultiSectionDigitalClock
                  value={draft}
                  onChange={handleDraftTimeChange}
                  ampm={false}
                  timeSteps={{ hours: 1, minutes: minutesStep }}
                  minTime={minDate || undefined}
                  maxTime={maxDate || undefined}
                  disableIgnoringDatePartForTimeValidation
                  sx={{
                    width: "auto",
                    flex: "0 0 auto",
                    mt: 1,
                    mr: 2,
                    borderBottom: "none",
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  px: 3,
                  pb: 2,
                }}
              >
                <button
                  type="button"
                  className="button button-small"
                  onClick={handleClose}
                >
                  {trans("buttons.cancel")}
                </button>
                <button
                  type="button"
                  className="button button-primary button-small"
                  onClick={handleConfirm}
                >
                  {trans("buttons.confirm")}
                </button>
              </Box>
            </>
          ) : (
            <DateCalendar
              value={date ? moment(date).locale(locale) : null}
              onChange={handleDateChange}
              maxDate={maxDate}
              minDate={minDate}
            />
          )}
        </Box>
      </Popover>
    </LocalizationProvider>
  )
}
